import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Box } from "@material-ui/core";
import {
  doc,
  onSnapshot,
  updateDoc,
} from "@firebase/firestore";
import { setTransaction } from "../../route/admin/transactionSlice";
import {
  db,
  largeCustomerTransactionPath,
} from "../../firebase";
import qs from "qs";
import { getImageURLs, deleteImages } from "../image/imgUtil";
import _ from "lodash";
import Loading from "../Loading";
import {
  tabLabels,
} from "../image/imageClassifications";

const TAG = "[TransactionImages]"

const TransactionImages = ({ location }) => {
  const history = useHistory();
  const { transaction } = useSelector(state => state.transaction);
  const [images, setImages] = useState(null);
  const dispatch = useDispatch();
  const [deleteUris, setDeleteUris] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");


  const classification = useMemo(() => {
    const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true })
    const classification = queryParams.classification;
    return classification
  }, [])

  /**
   * Sets transaction to the state, uses either the tx found in
   * router location parameters, or loads it from firebase
   * by parsing the ID from URL query parameters
   */
  useEffect(() => {
    let txUnSub;
    const transaction = location?.transaction;
    if (transaction) {
      dispatch(setTransaction(transaction));
    } else {
      const queryParams = qs.parse(location.search, {
        ignoreQueryPrefix: true,
      });
      const id = queryParams.id;
      if (!id) {
        console.warn("Transaction ID was not defined!");
        return;
      }
      txUnSub = onSnapshot(
        doc(db, largeCustomerTransactionPath, id),
        docSnapshot => {
          const data = docSnapshot.data();
          const key = docSnapshot.id;
          dispatch(setTransaction({ key: key, ...data }));
        },
        error => {
          console.warn(TAG, "Error while fetching transaction data", error);
        },
      );
    }
    return () => {
      if (txUnSub) {
        txUnSub();
      }
      dispatch(setTransaction(null));
    };
  }, [dispatch, location]);

  /**
   * Downloads classified images from firebase storage
   */
  useEffect(() => {
    // return early if tx doc is not defined
    if (!transaction) return;
    if (!classification) return;
    // basically keeps track are we mounted or not
    let isCanceled = false;

    const classifiedImages = transaction.pictures.classified.filter(img => img.classification === classification);
    getImageURLs(classifiedImages).then(
      resolvedImages => {
        const flatImages = [];
        for (const key in resolvedImages) {
          flatImages.push(...resolvedImages[key]);
        }
        if (!isCanceled && !_.isEqual(flatImages, images)) {
          setImages(flatImages);
        }
      },
    );
    return () => {
      isCanceled = true;
    };
  }, [transaction, images, classification]);

  const onConfirm = useCallback((urisToDelete, transaction) => {
    const oldImages = [...transaction.pictures.classified];
    const newImages = oldImages.filter(image => !urisToDelete.includes(image.imageUri));
    const newTx = { ...transaction };
    const newPictures = { ...newTx.pictures };
    newPictures.classified = newImages;
    newTx.pictures = newPictures;
    const txRef = doc(db, largeCustomerTransactionPath, transaction.key);
    updateDoc(txRef, {
      pictures: {
        classified: newImages
      }
    }).then(async () => {
      try {
        await deleteImages(urisToDelete)
      } catch (err) {
        console.warn("Failed to delete image files", err)
      }
      setSubmitting(false);
      dispatch(setTransaction(newTx));
    }).catch(err => {
      console.error(TAG, "Failed to delete images", err);
      setError("Kuvien poistaminen epäonnistui, yritä uudelleen");
    })
  }, [setSubmitting, dispatch, setError])


  return <>
    <Box m={5}>
      <div className="row mb-4">
        <div className="d-flex flex-row-reverse align-items-center">
          <Box pr={4}>
            <button className="btn btn-transparent" disabled={submitting} onClick={() => {
              history.goBack();
            }}>PERUUTA</button>
          </Box>
        </div>
      </div>
    </Box>

    <Box m={5}>
      <div>
        <h3 className="text-center mb-1">{tabLabels[classification] || ""}</h3>
      </div>
      <div className="d-flex flex-row flex-wrap justify-content-center">
        {!!images && images.map((image, index) => {
          const isMarked = image.imageUri in deleteUris;
          const bgStyles = isMarked ? "bg-danger" : ""
          return <div key={image.imageUri} className={"p-2 m-2 " + bgStyles} style={{}}>
            <img src={image.downloadURL} alt="" style={{ maxHeight: "550px" }} />
            <strong><p>Kuva {index + 1}</p></strong>
            <button className="btn btn-sm btn-primary" onClick={() => {
              setDeleteUris(prev => {
                const n = { ...prev };
                if (isMarked) {
                  delete n[image.imageUri];
                } else {
                  n[image.imageUri] = image.imageUri;
                }
                return n;
              })
            }}>{isMarked ? "Älä poista" : "Merkkaa poistettavaksi"}</button>
          </div>
        })}
      </div>


      <Box m={5}>
        <div className="row mb-4">
          <div className="d-flex flex-row-reverse align-items-center">
            <Box pr={4}>
              <button className="btn btn-primary" disabled={submitting} onClick={() => {
                if (submitting) return;
                const amount = Object.keys(deleteUris).length;
                const confirmed = window.confirm(`Haluatko varmasti poistaa ${amount} kuvaa?`);
                if (confirmed) {
                  setError("");
                  setSubmitting(true);
                  onConfirm(Object.keys(deleteUris), transaction);
                }
              }}>POISTA</button>
            </Box>
            <div className="mx-2">
              {submitting && <Loading />}
            </div>
            {error && <p className="text-danger">{error}</p>}
          </div>
        </div>
      </Box>

    </Box>
  </>
}

export default TransactionImages;
